'use memo';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import ActionButton from '@/components/Button/ActionButton';
import { DataTable } from '@/components/DataTable';
import { FillScreen, HStack } from '@/components/Layout';
import { TooltipWrapper } from '@/components/Tooltip/TooltipWrapper';
import { useToast } from '@/components/ui/use-toast';
import { handleApiError } from '@/lib/api/error';
import { orpc } from '@/lib/api/orpc';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { QUERY_PRESETS } from '@/lib/query';
import { useOutboundTaskTable } from './-hooks/useTable';
export const Route = createFileRoute('/_staff/settings/data-connector/outbound/data-pipeline/$id/_pipeline-detail/execution-results/')({
  component: OutboundExecutionResultsPage
});
function OutboundExecutionResultsPage() {
  const {
    id: pipelineId
  } = Route.useParams();
  const {
    t,
    i18n
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.dataConnector.outboundDataPipeline.detail.executionResults'
  });
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  const {
    table,
    displayState
  } = useOutboundTaskTable(pipelineId);

  // 実行計画（運用モード / トリガーモード）で手動実行の可否を判定する（IN と同じ挙動）。
  const detailQuery = useQuery({
    ...orpc.outboundDataPipeline.get.queryOptions({
      input: {
        id: pipelineId
      }
    }),
    ...QUERY_PRESETS.detail()
  });
  const isPaused = detailQuery.data?.executionPlan?.operationMode === 'PAUSED';
  const isScheduled = detailQuery.data?.executionPlan?.triggerMode === 'SCHEDULE';
  const isExecuteDisabled = isPaused || isScheduled;
  const executeDisabledReason = isPaused ? t('executeDisabledPaused') : isScheduled ? t('executeDisabledScheduled') : undefined;
  const executeMutation = useMutation(orpc.outboundDataPipelineTask.executeManual.mutationOptions({
    onSuccess: () => {
      // バッチ起動〜Task 作成までラグがあり即時には一覧へ反映されないため、
      // 「実行を開始した・時間がかかる・画面は自動更新されない」ことをトーストで明示する。
      toast({
        title: t('executeStartedTitle'),
        description: t('executeStartedDescription')
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.outboundDataPipelineTask.listTasks.key({
          input: {
            pipelineId
          }
        })
      });
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, {
        language: i18n.language
      });
      toast({
        description: message,
        variant: 'destructive'
      });
    }
  }));
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <HStack className="justify-end gap-2">
        <TooltipWrapper content={executeDisabledReason}>
          <ActionButton variant="secondary" icon="RocketIcon" onClick={() => executeMutation.mutate({
          pipelineId
        })} isLoading={executeMutation.isPending} disabled={isExecuteDisabled}>
            {t('execute')}
          </ActionButton>
        </TooltipWrapper>
      </HStack>
      <div className="min-h-0 flex-1 overflow-auto">
        <DataTable table={table} displayState={displayState} onRowClick={null} />
      </div>
    </FillScreen>;
}